import { BCMSCurrency, Link, useRoute, Branding, BrandingStyle ,DisabledPrompt} from "bcms-react";
import { WidgetContainer, WidgetView } from 'components/shared';
import { CODAccount, CheckingAccount, LoanAccount, SavingsAccount } from "model";
import React from "react";
import "./styles.css";
import "./mobile.css";
import AccountSummaryBrandableLinksWidget from "./components/AccountSummaryBrandableLinksWidget";
import { SVGAsset } from "res";
import Util from "classes/Util";
/**
 * @function AccountSummaryWidget
 * @return {React.Component}
 * @exported true
 * @description This is the quick transfer widget
 */
export default function AccountSummaryWidget(props) {
  // Create the Summary items for each type of account
  const checkingAccts = CheckingAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } amount={ account.availableBalance } label={ account.balanceLabel } />, []);
  const savingsAccts = SavingsAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } amount={ account.availableBalance } label={ account.balanceLabel } />, []);
  const codAccounts = CODAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } amount={ account.availableBalance } label={ account.balanceLabel } />, []);
  const loanAccts = LoanAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } amount={ account.payoffAmount } label={ account.balanceLabel } />, []);

  return(
    <WidgetContainer className={"account-summary-widget "+props.className??""}>
      <WidgetView text="Account Summary" dataPoint="Account_Summary_Header">
        <AccountSummaryBrandableLinksWidget/>
        <AccountSummaryTable>
          <AccountSummaryHeader dataPoint="Checking_Accounts" text="Checking Accounts" />
            { checkingAccts }
            { !Util.inMabi && 
              <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                <>
                  <DisabledPrompt>
                    <Link>
                      <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                    </Link>
                  </DisabledPrompt>
                </>
              </Branding>
            }
        
          <AccountSummaryHeader text="Saving Accounts" />
            { savingsAccts }

          <AccountSummaryHeader text="Certificate of Deposit" />
            { codAccounts }

          <AccountSummaryHeader dataPoint="Loans" text="Loans" />
            { loanAccts }
        </AccountSummaryTable>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function AccountSummaryView
 * @param {Account} account - Instance of Account
 * @param {bcmsCurrency} amount - Amount value
 * @param {STRING} label - The label for the summary item
 * @exported true
 * @description How an account is presented in account summary
 * @note If amount is not an instance of BCMSCurrency an amount of "$0.00" is shown
 */
export function AccountSummaryView({ account, amount, label }) {
  const balance = amount instanceof BCMSCurrency ? amount.format() : BCMSCurrency.toCurrency(0);

  return (
    <SummaryItem id={ account.id } accountName={ account.nickname } availableBalance={ balance } accountNumber={ account.number } balanceLabel={ label }/>
  );
}

/**
 * @function AccountSummaryTable
 * @param {OBJECT} props 
 * @return {React.Component}
 * @exported true
 * @description This is the table for all of the table components
 * @example
 * <AccountSummaryTable>
 *  <AccountSummaryHeader text="This is a header" />
 * </AccountSummaryTable>
 */
export function AccountSummaryTable(props) {
  return(
    <table className="account-summary-table">
      {props.children}
    </table>
  );
}

/**
 * @function AccountSummaryHeader
 * @param {OBJECT} props
 * @param {STRING} text - This is the white text that is shown in the blue header.
 * @return {React.Component}
 * @exported true
 * @description The colored header row that shows the account types
 * @example
 * <AccountSummaryHeader text="This is a sample header">
 */
export function AccountSummaryHeader(props) {
  const text = props.text ?? "";
  const dataPoint = props.dataPoint ?? "";

  return (
    <thead>
      <tr>
        <th className="text-left" colSpan="2">
            <span data-point={dataPoint}>
            {text}
          </span>
        </th>
      </tr>
    </thead>
  );
}

/**
 * @function SummaryItem
 * @param {OBJECT} props 
 * @param {STRING} accountName - The account name
 * @param {STRING} availableBalance - Available balance for the account.
 * @param {STRING} accountNumber - Short four digit account number.
 * @return {React.Component}
 * @exported true
 * @description This is the component that shows each account and the account details.
 * @example
 * <SummaryItem accountName="Example Account" availableBalance="$7,498.24" accountNumber="*6238">
 */
export function SummaryItem(props) {
  const accountName = props.accountName ?? "";
  const availableBalance = props.availableBalance ?? "";
  const accountNumber = props.accountNumber ?? "";
  const balanceLabel = props.balanceLabel ?? "Available Balance";
  const accountID = props.id ?? "";
  const navigate = useRoute();  
  let onClick = () => {        
          navigate(`/accountdetails/${accountID}`);        
  }
  return(
    <tbody className="clickable" onClick={onClick}>
      <tr className="summary-item " >
        <td className="account-name">
          <BrandingStyle style={{"color": "acct_name_abi22"}} element={
             <span data-point={ `Account_Name_${ accountID }` }>
              {accountName}
            </span>
          }/>
        </td>
        <td className="text-right available-balance">
          <span data-point={ `Account_Balance_${ accountID }` }>
            {availableBalance}
          </span>
        </td>
      </tr>
      <tr className="summary-item">
        <td className="sub-text">
          <span data-point={ `Account_Number_${ accountID }` }>
            {accountNumber}
          </span>
        </td>
        <td className="text-right">
          <span>
            {balanceLabel}
          </span>
        </td>
      </tr>
    </tbody>
  );
}